@let portalOrder = portalOrder$ | async;

@let continueButtonLabel = ancillaryUpsell ? 'Upgrade' : 'Continue';

@if (portalOrder) {
  <stepper
    [step]="Steps.BookingOptions"
    [isChangeMode]="portalOrder.isChange"
    [tiers]="true"
    [ancillaryUpsell]="ancillaryUpsell"
  />

  <div
    (pointerdown)="pointerDown($event)"
    (pointerup)="pointerUp($event)"
    class="d-flex flex-column p-4 p-md-0 py-md-6 swipe"
  >
    <h2 class="semi-bold">Choose your booking option</h2>

    <p class="mt-2">Flexibility, protection & expert support for this trip</p>

    <ds-tabs
      (selectTab)="selectedSectionTab = $event"
      [tabs]="sectionTabs"
      [selectedTab]="selectedSectionTab"
      class="section-tab mt-4 mb-6"
    />

    <ds-tabs
      (selectTab)="selectTierTab($event)"
      [tabs]="tierTabs"
      [selectedTab]="selectedTierTab"
      class="secondary py-4 tabs"
    />

    @if (selectedSectionTab.value) {
      <div class="d-flex g-2">
        @for (tier of tiers; track tier) {
          <ng-container *ngTemplateOutlet="tierCardTemplate; context: { tier }" />
        }
      </div>
    } @else {
      <ng-container *ngTemplateOutlet="tableTemplate" />
    }

    <ng-container *ngTemplateOutlet="tierCardsTemplate" />

    @if (!selectedTier.disabled) {
      @let soldAmountPerAdult =
        ancillaryUpsell
          ? selectedTier.soldAmountPerAdult
          : selectedTier.soldAmountPerAdult + portalOrder.soldAmountPerAdult;

      @if (soldAmountPerAdult && !isEmbedded) {
        <div class="action m-4 d-md-none">
          <ds-button
            (click)="continue()"
            [id]="selectedTier.value || 'base'"
            [fullWidth]="true"
            data-id="continue"
            [label]="
              continueButtonLabel + ' for ' + (soldAmountPerAdult | portalCurrency) + '/passenger'
            "
          />
        </div>
      }
    }

    @let isYourPlan = selectedTier && selectedTier.isYourPlan;

    <div class="d-flex flex-column flex-md-row g-2 mt-4 mt-md-10">
      <ds-button
        (click)="back()"
        [fullWidth]="true"
        variant="secondary"
        leadingIcon="chevronLeft"
        class="w-md-unset"
        data-id="back"
        label="Back"
      />

      @if (ancillaryUpsell && isYourPlan && !isEmbedded) {
        <ds-button
          (click)="continue()"
          [fullWidth]="true"
          class="w-md-unset ml-md-auto"
          data-id="continue"
          label="Continue"
        />
      }
    </div>
  </div>
}

<ng-template #tierCardsTemplate>
  @for (row of rows; track row) {
    <div class="mt-1 p-4 d-flex flex-column card d-md-none">
      <h3 class="semi-bold">{{ row.label }}</h3>

      @for (i of row.items; track i) {
        <div class="d-flex align-items-start justify-content-between mt-4">
          <div>
            <p class="d-inline">{{ i.label }}</p>

            @if (i.tooltip) {
              <ng-container
                *ngTemplateOutlet="hintTemplate; context: { tooltip: i.tooltip, label: i.label }"
              />
            }
          </div>

          <div class="value">
            <ng-container
              *ngTemplateOutlet="
                valueTemplate;
                context: {
                  value: selectedTier[row.key][i.key],
                  tooltip: selectedTier[row.key][i.key + 'Tooltip'],
                }
              "
            />
          </div>
        </div>
      }
    </div>
  }
</ng-template>

<ng-template #tierCardTemplate let-tier="tier">
  @let soldAmountPerAdult =
    ancillaryUpsell
      ? tier.soldAmountPerAdult
      : tier.soldAmountPerAdult + portalOrder?.soldAmountPerAdult;

  <div
    (click)="selectTier(tier)"
    [ngClass]="{
      'd-flex selected': tier === selectedTier && !tier.disabled,
      'd-none': tier !== selectedTier,
      disabled: tier.disabled,
    }"
    class="d-md-flex flex-column flex-1 p-4 card"
    [class.cursor-pointer]="!tier.disabled"
  >
    <div class="d-flex justify-content-between">
      <div class="d-flex flex-column">
        <p [ngClass]="tier.value | lowercase" class="mt-1 badge">{{ tier.badge || '&nbsp;' }}</p>

        <h1 class="semi-bold mt-1">{{ tier.label }}</h1>

        @if (soldAmountPerAdult) {
          <h3 class="mt-2">
            <b>{{ soldAmountPerAdult | portalCurrency }}</b
            >/passenger
          </h3>
        }
      </div>

      <img
        [src]="'/assets/images/3d-' + tier.img + '.webp'"
        [alt]="tier.img"
        width="100"
        height="100"
        class="img"
      />
    </div>

    <div class="mt-4 d-flex items">
      <img src="/assets/icons/airplane_ticket.svg" alt="airplane_ticket" width="20" height="20" />
      <p>{{ tier.base.protection }}</p>

      <img
        src="/assets/icons/airplanemode_inactive.svg"
        alt="airplanemode_inactive"
        width="20"
        height="20"
      />
      <p>{{ tier.base.cancellation }}</p>

      <img src="/assets/icons/money.svg" alt="money" width="20" height="20" />
      <p>{{ tier.base.changes }}</p>

      <img src="/assets/icons/support_agent.svg" alt="support_agent" width="20" height="20" />
      <p>{{ tier.base.concierge }}</p>

      <img src="/assets/icons/payments.svg" alt="payments" width="20" height="20" />
      <p>{{ tier.base.cashback }}</p>
    </div>

    @if (tier.disabled) {
      <p class="semi-bold mt-auto mb-3">Currently unavailable</p>
    } @else if (tier.isYourPlan) {
      <p class="semi-bold mb-3 mt-5 mt-md-auto">Your Plan</p>
    } @else if (tier.includedInEssential) {
      <p class="semi-bold mb-3 mt-5 mt-md-auto">Included in Essential</p>
    } @else if (!isEmbedded) {
      <div class="d-none d-md-flex mt-auto pt-4 w-100">
        <ds-button
          (click)="selectTier(tier); continue()"
          [variant]="tier !== selectedTier ? 'secondary' : 'primary'"
          [id]="tier.value || 'base'"
          [fullWidth]="true"
          class="w-100"
          data-id="continue"
          [label]="continueButtonLabel + ' for ' + (soldAmountPerAdult | portalCurrency) + ' pp'"
        />
      </div>
    }
  </div>
</ng-template>

<ng-template #valueTemplate let-value="value" let-tooltip="tooltip">
  @switch (value) {
    @case (true) {
      <img src="/assets/icons/check-blue.svg" alt="check-blue" width="20" height="20" />
    }
    @case (false) {
      <img src="/assets/icons/close_travalign.svg" alt="close_travalign" width="20" height="20" />
    }
    @default {
      <p class="d-inline">{{ value }}</p>
    }
  }
  @if (tooltip) {
    <ng-container *ngTemplateOutlet="hintTemplate; context: { tooltip }" />
  }
</ng-template>

<ng-template #hintTemplate let-tooltip="tooltip" let-label="label">
  <img
    [tooltip]="tooltipTemplate"
    [modal]="tooltipTemplate"
    src="/assets/icons/info.svg"
    alt="info"
    width="20"
    height="20"
    class="d-inline ml-1 info"
  />

  <ng-template #tooltipTemplate>
    <div class="m-4 m-md-0 w-600">
      @if (label) {
        <p class="mb-2 semi-bold">{{ label }}</p>
      }
      <p>{{ tooltip }}</p>
    </div>
  </ng-template>
</ng-template>

<ng-template #tableTemplate>
  <div class="d-flex sticky">
    <div class="flex-1 p-4 label">&nbsp;</div>

    @for (tier of tiers; track tier; let first = $first; let last = $last) {
      <div
        (click)="selectTier(tier)"
        [ngClass]="{
          first: first,
          last: last,
          selected: tier === selectedTier && !tier.disabled,
          disabled: tier.disabled,
        }"
        class="flex-1 ml-1 p-4 cell head d-flex flex-column justify-content-between"
        [class.cursor-pointer]="!tier.disabled"
      >
        <div class="d-flex flex-column">
          <p [ngClass]="tier.value | lowercase" class="mt-1 badge">{{ tier.badge || '&nbsp;' }}</p>

          <h2 class="semi-bold mt-1">{{ tier.label }}</h2>
        </div>

        <img
          [src]="'/assets/images/3d-' + tier.img + '.webp'"
          [alt]="tier.img"
          width="80"
          height="80"
          class="img"
        />

        @if (tier.disabled) {
          <p class="semi-bold mt-auto mb-2">Currently unavailable</p>
        } @else if (tier.isYourPlan) {
          <p class="semi-bold mt-auto mb-2">Your Plan</p>
        } @else if (tier.includedInEssential) {
          <p class="semi-bold mt-auto mb-2">Included in Essential</p>
        } @else if (!isEmbedded) {
          @let soldAmountPerAdult =
            ancillaryUpsell
              ? tier.soldAmountPerAdult
              : tier.soldAmountPerAdult + portalOrder?.soldAmountPerAdult;

          <ds-button
            (click)="selectTier(tier); continue()"
            [variant]="tier !== selectedTier ? 'secondary' : 'primary'"
            [id]="tier.value || 'base'"
            size="s"
            [fullWidth]="true"
            class="mt-6 w-100"
            data-id="continue"
            [label]="continueButtonLabel + ' for ' + (soldAmountPerAdult | portalCurrency) + ' pp'"
          />
        }
      </div>
    }
  </div>

  @for (row of rows; track row; let first = $first) {
    <div [ngClass]="{ 'mt-1': !first }" class="section">
      <div class="d-flex">
        <div class="flex-1 p-4 cell label">
          <h3 class="semi-bold">{{ row.label }}</h3>
        </div>

        @for (tier of tiers; track tier) {
          <div [ngClass]="{ selected: selectedTier === tier }" class="flex-1 ml-1 p-4 cell">
            &nbsp;
          </div>
        }
      </div>

      @for (i of row.items; track i) {
        <div (mouseenter)="hoveredRowItem = i" (mouseleave)="hoveredRowItem = null" class="d-flex">
          <div
            [ngClass]="{ hovered: hoveredRowItem === i }"
            class="flex-1 p-4 cell label d-flex justify-content-between"
          >
            <p>{{ i.label }}</p>

            @if (i.tooltip) {
              <ng-container
                *ngTemplateOutlet="hintTemplate; context: { tooltip: i.tooltip, label: i.label }"
              />
            }
          </div>

          @for (tier of tiers; track tier) {
            <div
              [ngClass]="{ hovered: hoveredRowItem === i, selected: selectedTier === tier }"
              class="flex-1 ml-1 p-4 cell text-align-center"
            >
              <ng-container
                *ngTemplateOutlet="
                  valueTemplate;
                  context: {
                    value: tier[row.key][i.key],
                    tooltip: tier[row.key][i.key + 'Tooltip'],
                  }
                "
              />
            </div>
          }
        </div>
      }
    </div>
  }
</ng-template>

<ng-template #upsellOptionModalTemplate>
  <upsell-option-modal
    [upgradePricePerPassenger]="essentialUpsellPricePerPassenger"
    (hideModal)="hideModal()"
    (upgrade)="upgrade()"
    (continue)="hideModal(); checkSession()"
    (review)="review()"
  />
</ng-template>

<ng-template #askToSignInTemplate>
  <ask-to-sign-in-modal
    (hideModal)="hideModal()"
    (continueAsGuest)="redirect(); hideModal()"
    (showSignInModal)="showSignInModal()"
  />
</ng-template>
